import { VENDOR_CATEGORIES } from "@compass/shared/utils";
import { CheckIcon } from "@heroicons/react/20/solid";
import {
    ChevronDownIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "@tanstack/react-router";
import { useSearch } from "@tanstack/react-router";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { vendorList } from "app/router";
import {
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    Table as T,
    TablePagination,
    TableView,
} from "components/elements";
import AlertDialogDemo from "components/elements/DeleteModal";
import { TableSortIconVendor as TableSortIcon } from "components/elements/TableSortIconVendor";
import { TrendingRow } from "components/layout/TrendingRow";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useSortToggler } from "hooks/useSortToggler";
import { trpc } from "lib/trpc";
import {
    ClientCurrentUser,
    VendorList as VendorListType,
    VendorListing,
} from "@compass/shared/types";
import { NewVendorForm } from "./NewVendorForm";
import { VendorTableEntry } from "./VendorTableEntry";

export const VendorList = () => {
    const [isModalOpen, setModalOpen] = useState(false);
    const input = useSearch({ from: vendorList.id });
    const { mutateAsync: mutateDeleteVendorAsync } = trpc.vendors.deleteVendor.useMutation();
    const navigate = useNavigate();
    const currentUser = useCurrentUser();
    const isReadOnly = currentUser?.role === "READONLY_USER";
    const searchValue = input.search ?? "";
    const selectedCategories = input.categories ?? [];
    const [deletePopup, setDeletePopup] = useState<{ id: number | null; isOpen: boolean }>({
        id: null,
        isOpen: false,
    });
    const [editPopup, setEditPopup] = useState<VendorListing | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [searchDraft, setSearchDraft] = useState(input.search ?? "");
    const queryClient = trpc.useContext();
    useEffect(() => {
        setSearchDraft(input.search ?? "");
    }, [input.search]);
    const useVendors = (input: VendorListType, currentUser?: ClientCurrentUser) => {
        if (currentUser?.role === "GUEST") {
            return {
                data: { vendors: [] as VendorListing[], total: 0, totalFound: 0 },
                isLoading: false,
                nothingFound: true,
                total: 0,
                totalFound: 0,
            };
        }
        const { data, isPending: isLoading, error } = trpc.vendors.latest.useQuery(input);
        return {
            error,
            isLoading,
            data,
            total: data?.total ?? 0,
            totalFound: data?.totalFound ?? 0,
        };
    };
    const { data, isLoading, error, nothingFound } = useVendors(
        {
            ...input,
            search: searchValue,
            categories: selectedCategories,
            sort: input.sort ?? "desc",
            sortBy: input.sortBy ?? "createdAt",
        },
        currentUser,
    );
    const onSearchChange = (search: string) => {
        navigate({
            search: {
                ...input,
                search: search === "" ? undefined : search,
                page: undefined,
            },
            replace: true,
        });
    };
    const onCategoryChange = (categories: string[]) => {
        navigate({
            search: {
                ...input,
                categories: categories.length === 0 ? undefined : categories,
                page: undefined,
            },
            replace: true,
        });
    };
    const onClearFilters = () => {
        navigate({ search: {}, replace: true });
    };
    const onChangePage = (page: number | undefined) => {
        navigate({ search: { ...input, page }, replace: true });
    };
    const onChangePageSize = (pageSize: number | undefined) => {
        const oldPageSize = input.pageSize ?? 10;
        const newPageSize = pageSize ?? 10;
        const currentPage = input.page ?? 0;
        const totalItems = data?.totalFound ?? 0;
        const firstItemIndex = currentPage * oldPageSize;
        let newPage = Math.floor(firstItemIndex / newPageSize);
        const maxPossiblePage = Math.ceil(totalItems / newPageSize) - 1;
        if (newPage > maxPossiblePage) {
            newPage = Math.max(0, maxPossiblePage);
        }
        navigate({
            search: {
                ...input,
                pageSize: pageSize === 10 ? undefined : pageSize,
                page: newPage === 0 ? undefined : newPage,
            },
            replace: true,
        });
    };
    const sortToggler = useSortToggler({
        defaultSortBy: "createdAt",
        defaultSort: "desc",
    });
    const toggleSortField = (field: string) => {
        const [nextSortBy, nextSort] = sortToggler({
            currentSortBy: input.sortBy,
            currentSort: input.sort,
            nextSortBy: field,
        });
        navigate({
            search: { ...input, sortBy: nextSortBy as any, sort: nextSort, page: 0 },
            replace: true,
        });
    };
    const handleDeleteVendor = async (id: number) => {
        mutateDeleteVendorAsync(id, {
            onSuccess: () => {
                queryClient.vendors.latest.invalidate();
                setDeletePopup({ id: null, isOpen: false });
            },
            onError: () => {
                console.error("Error");
                enqueueSnackbar({ variant: "error" });
            },
        });
    };
    const handleExportPDF = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            // Fetch all vendors with current filters
            const { vendors: allVendorsData } = await queryClient.vendors.all.fetch({
                search: searchValue,
                categories: selectedCategories,
                ...(input.sort && { sort: input.sort }),
                ...(input.sortBy && { sortBy: input.sortBy }),
            });
            if (!allVendorsData || allVendorsData.length === 0) {
                enqueueSnackbar({
                    message: "No vendors to export",
                    variant: "warning",
                });
                return;
            }
            // Create PDF
            const doc = new jsPDF();
            // Add title
            doc.setFontSize(18);
            doc.text("Vendor List", 14, 20);
            // Add filter info if applicable
            let yPos = 30;
            doc.setFontSize(10);
            if (searchValue) {
                doc.text(`Search: ${searchValue}`, 14, yPos);
                yPos += 6;
            }
            if (selectedCategories.length > 0) {
                doc.text(`Categories: ${selectedCategories.join(", ")}`, 14, yPos);
                yPos += 6;
            }
            // Prepare table data
            const tableData = allVendorsData.map((vendor: any) => [
                vendor.name || "",
                vendor.contactName || "",
                vendor.category || "",
                vendor.website || "",
            ]);
            // Generate table
            autoTable(doc, {
                head: [["Company Name", "Contact", "Category", "Website"]],
                body: tableData,
                startY: yPos + 5,
                styles: { fontSize: 9 },
                headStyles: { fillColor: [66, 66, 66] },
                didDrawCell: (data) => {
                    // Add hyperlinks to website column
                    if (data.column.index === 3 && data.cell.section === "body") {
                        const website = data.cell.raw as string;
                        if (website) {
                            // Ensure website has protocol
                            const url = website.startsWith("http") ? website : `https://${website}`;
                            doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
                                url,
                            });
                        }
                    }
                },
            });
            // Add footer at the bottom of the page
            const pageHeight = doc.internal.pageSize.height;
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("Powered by Compass CFO Solutions", 14, pageHeight - 10);
            // Save the PDF
            const fileName = `vendors_${new Date().toISOString().split("T")[0]}.pdf`;
            doc.save(fileName);
            enqueueSnackbar({
                message: "PDF exported successfully",
                variant: "success",
            });
        } catch (error) {
            console.error("Error exporting PDF:", error);
            enqueueSnackbar({
                message: "Error exporting PDF",
                variant: "error",
            });
        } finally {
            setIsExporting(false);
        }
    };
    const handleExportExcel = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            // Fetch Excel file from server
            const base64Data = await queryClient.vendors.exportExcel.fetch({
                search: searchValue,
                categories: selectedCategories,
                ...(input.sort && { sort: input.sort }),
                ...(input.sortBy && { sortBy: input.sortBy }),
            });
            // Convert base64 to blob
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            // Generate filename with current date
            const fileName = `vendors_${new Date().toISOString().split("T")[0]}.xlsx`;
            // Create download link and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            enqueueSnackbar({
                message: "Excel file exported successfully",
                variant: "success",
            });
        } catch (error) {
            console.error("Error exporting Excel:", error);
            enqueueSnackbar({
                message: "Error exporting Excel file",
                variant: "error",
            });
        } finally {
            setIsExporting(false);
        }
    };
    const Icon = ({ field }: { field: string }) => {
        const search = useSearch({ from: vendorList.id });
        const sort = search.sort ?? "desc";
        const sortBy = search.sortBy ?? "createdAt";
        return <TableSortIcon sort={sort} sortBy={sortBy} field={field} />;
    };
    const handleEdit = (data: VendorListing) => {
        setEditPopup(data);
        setModalOpen(true);
    };
    const hasSearch = Boolean(searchValue);
    const hasCategories = selectedCategories.length > 0;
    const hasActiveFilters = hasSearch || hasCategories;
    return (
        <Card>
            <div className="mb-3">
                <TrendingRow filterType="vendor" title="Trending Vendors" limit={5} />
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <div className="shrink-0 text-left font-sans text-[28px] font-semibold tracking-normal text-[#424242]">
                        Vendors
                    </div>
                    {!isReadOnly && (
                        <>
                            {/* PRTL-1314: primary vendor search lives inline next to the
                                title (matches the redesign) — no longer hidden inside the
                                Filters dropdown, which is now categories-only. */}
                            <div className="relative w-full flex-1 md:max-w-xl">
                                <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchDraft}
                                    onChange={(e) => setSearchDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            onSearchChange(searchDraft);
                                        } else if (e.key === "Escape" && searchDraft) {
                                            e.preventDefault();
                                            setSearchDraft("");
                                            onSearchChange("");
                                        }
                                    }}
                                    placeholder="Search vendors..."
                                    className="h-[38px] w-full rounded-md border border-[#d4d5d5] bg-white pl-8 pr-20 text-sm text-[#424242] placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-sky-500"
                                />
                                <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
                                    {searchDraft && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchDraft("");
                                                onSearchChange("");
                                            }}
                                            aria-label="Clear search"
                                            className="flex h-5 w-5 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => onSearchChange(searchDraft)}
                                        disabled={searchDraft === searchValue}
                                        className="rounded bg-sky-500 px-2 py-0.5 text-xs font-semibold text-white hover:bg-sky-600 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 md:ml-auto">
                                <Dropdown
                                    content={
                                        <DropdownContent
                                            arrow={false}
                                            align="start"
                                            className="w-[18rem] min-w-[18rem] p-1"
                                            bordered
                                        >
                                            <DropdownMenu.Label className="px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                Categories
                                            </DropdownMenu.Label>
                                            {VENDOR_CATEGORIES.map((c) => {
                                                const checked = selectedCategories.includes(c);
                                                return (
                                                    <DropdownMenu.CheckboxItem
                                                        key={c}
                                                        checked={checked}
                                                        onCheckedChange={(next) => {
                                                            onCategoryChange(
                                                                next
                                                                    ? [...selectedCategories, c]
                                                                    : selectedCategories.filter(
                                                                          (x) => x !== c,
                                                                      ),
                                                            );
                                                        }}
                                                        onSelect={(e) => e.preventDefault()}
                                                        className="radix-highlighted:outline-0 radix-highlighted:bg-[#2DA7E0] radix-highlighted:text-white flex h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm text-[#424242]"
                                                    >
                                                        <span className="flex h-4 w-4 items-center justify-center rounded border border-gray-300 bg-white">
                                                            <DropdownMenu.ItemIndicator>
                                                                <CheckIcon className="h-3 w-3 text-sky-600" />
                                                            </DropdownMenu.ItemIndicator>
                                                        </span>
                                                        {c}
                                                    </DropdownMenu.CheckboxItem>
                                                );
                                            })}
                                        </DropdownContent>
                                    }
                                >
                                    <Button
                                        variant="secondary"
                                        className="flex h-[38px] items-center gap-2 px-3"
                                    >
                                        <FunnelIcon className="h-4 w-4" />
                                        Filters
                                    </Button>
                                </Dropdown>
                                <div className="flex items-center gap-2">
                                    <Dropdown
                                        content={
                                            <DropdownContent
                                                arrow={false}
                                                align="end"
                                                className="w-auto min-w-[160px]"
                                                bordered
                                            >
                                                <DropdownItem
                                                    size="smaller"
                                                    onSelect={(e) => {
                                                        e.preventDefault();
                                                        handleExportPDF();
                                                    }}
                                                >
                                                    Export PDF
                                                </DropdownItem>
                                                <DropdownItem
                                                    size="smaller"
                                                    onSelect={(e) => {
                                                        e.preventDefault();
                                                        handleExportExcel();
                                                    }}
                                                >
                                                    Export Excel
                                                </DropdownItem>
                                            </DropdownContent>
                                        }
                                    >
                                        <Button
                                            variant="secondary"
                                            className="flex h-[38px] items-center gap-2 px-3"
                                            disabled={isExporting}
                                        >
                                            {isExporting ? "Exporting..." : "Export"}
                                            <ChevronDownIcon className="h-4 w-4" />
                                        </Button>
                                    </Dropdown>
                                    <Button
                                        variant="primary"
                                        className="h-[38px]"
                                        onClick={() => {
                                            setModalOpen(true);
                                            setEditPopup(null);
                                        }}
                                    >
                                        + Add New Vendor
                                    </Button>
                                    <NewVendorForm
                                        isOpen={isModalOpen}
                                        onClose={() => setModalOpen(false)}
                                        editForm={editPopup}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
                {!isReadOnly && hasActiveFilters && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {hasSearch && (
                            <div className="flex items-center gap-2 rounded-md border border-[#d4d5d5] bg-white px-3 py-1.5 text-xs text-[#424242]">
                                <span className="text-gray-500">Search</span>
                                <span className="font-medium">"{searchValue}"</span>
                                <button
                                    type="button"
                                    onClick={() => onSearchChange("")}
                                    className="text-gray-400 hover:text-gray-700"
                                    aria-label="Clear search"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        )}
                        {selectedCategories.map((category) => (
                            <div
                                key={category}
                                className="flex items-center gap-2 rounded-md border border-[#d4d5d5] bg-white px-3 py-1.5 text-xs text-[#424242]"
                            >
                                <span className="font-medium">{category}</span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        onCategoryChange(
                                            selectedCategories.filter((c) => c !== category),
                                        )
                                    }
                                    className="text-gray-400 hover:text-gray-700"
                                    aria-label={`Remove ${category}`}
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={onClearFilters}
                            className="ml-1 text-sm font-medium text-gray-500 hover:text-gray-700"
                        >
                            Clear all
                        </button>
                    </div>
                )}
            </div>
            <TableView
                data={data}
                emptyCollection={<div>There are no vendors yet. Try adding one?</div>}
                error={error}
                footer={
                    <TablePagination
                        total={data?.totalFound}
                        page={input.page}
                        onChangePage={onChangePage}
                        pageSize={input.pageSize}
                        onChangePageSize={onChangePageSize}
                    />
                }
                nothingFound={nothingFound}
                isInitialLoading={isLoading}
                isFiltering={Object.keys(input).filter((key) => key !== "_empty").length > 0}
                onClearFilters={onClearFilters}
            >
                {(data) => {
                    if (data.totalFound === 0) {
                        return (
                            <div className="flex h-32 items-center justify-center">
                                <div className=" text-gray-500">
                                    No vendors found. Try a different search.
                                </div>
                            </div>
                        );
                    }
                    return (
                        <T.Table className="p-3">
                            <T.Head className="">
                                <T.HeadRow className="">
                                    <T.Cell onClick={() => toggleSortField("name")}>
                                        <div className="flex cursor-pointer flex-row text-white">
                                            Company Name <Icon field="name" />
                                        </div>
                                    </T.Cell>
                                    <T.Cell
                                        onClick={() => toggleSortField("contactName")}
                                        className="w-20"
                                    >
                                        <div className="flex cursor-pointer flex-row text-white">
                                            Contact
                                            <Icon field="contactName" />
                                        </div>
                                    </T.Cell>
                                    <T.Cell onClick={() => toggleSortField("category")}>
                                        <div className="flex cursor-pointer flex-row text-white">
                                            Category <Icon field="category" />
                                        </div>
                                    </T.Cell>
                                    <T.Cell>
                                        <div className="flex flex-row text-white">Website</div>
                                    </T.Cell>
                                    <T.Cell>
                                        <div className="flex flex-row text-white">Favorites</div>
                                    </T.Cell>
                                    <T.Cell>
                                        <div className="flex flex-row text-white">Actions</div>
                                    </T.Cell>
                                    <T.Cell>
                                        <div></div>
                                    </T.Cell>
                                </T.HeadRow>
                            </T.Head>
                            <T.Body>
                                {data?.vendors?.map((vendor) => (
                                    <VendorTableEntry
                                        setDeletePopup={setDeletePopup}
                                        key={vendor.id}
                                        vendor={vendor}
                                        handleEdit={handleEdit}
                                    />
                                ))}
                            </T.Body>
                        </T.Table>
                    );
                }}
            </TableView>
            <AlertDialogDemo
                id={deletePopup.id}
                open={deletePopup.isOpen}
                setOpen={setDeletePopup}
                handleDelete={handleDeleteVendor}
            />
        </Card>
    );
};
